import { createElement, Fragment } from 'react';
import type { FC, ReactElement, ReactNode } from 'react';
import spacing from '../spacing';
import borderWidth from '../borderWidth';
import { vars } from '../../theme';
import {
    BREAKPOINT_ORDER,
    isResponsive,
    responsiveCss,
    responsiveValueCss
} from '../responsive';
import { hideScrollbar as hideScrollbarMixin } from '../scrollbar';
import type { Breakpoint, Responsive } from '../responsive';
import type {
    LayoutBaseProps,
    LayoutSpacingProps,
    LayoutSizingProps,
    LayoutChildProps,
    LayoutDecorativeProps,
    RadiusKey,
    BackgroundColorToken,
    ShadowKey,
    SpacingKey,
    BorderStyleKey,
    BorderWidthKey
} from './layout.types';

export { isResponsive, responsiveCss, responsiveValueCss } from '../responsive';

/**
 * Resolves a spacing key to its CSS value.
 * Returns the value from the spacing scale (e.g., '24' → '24px').
 */
export function resolveSpacing(
    key: SpacingKey | undefined
): string | undefined {
    if (key === undefined) {
        return undefined;
    }
    return spacing[key];
}

/**
 * Shorthand for responsiveCss with spacing token resolution.
 * Resolves HBUI spacing keys to px values before generating CSS.
 */
export function responsiveSpacingCss(
    value: Responsive<SpacingKey> | undefined,
    cssProperties: string[]
) {
    return responsiveCss(value, cssProperties, resolveSpacing);
}

function pickLayoutProps<T, K extends keyof T>(
    props: T,
    keys: readonly K[]
): [Pick<T, K>, Omit<T, K>] {
    const picked: Record<string, unknown> = {};
    const rest = { ...props } as Record<string, unknown>;
    for (const key of keys) {
        if (key in rest) {
            picked[key as string] = rest[key as string];
            delete rest[key as string];
        }
    }
    return [picked as Pick<T, K>, rest as Omit<T, K>];
}

/** LayoutBaseProps keys that resolve to CSS (as opposed to native/React props like children, data-testid, displayName). */
const LAYOUT_BASE_KEYS: Array<keyof LayoutBaseProps> = [
    'position',
    'inset',
    'top',
    'right',
    'bottom',
    'left',
    'zIndex',
    'cursor',
    'pointerEvents',
    'userSelect',
    'listStyle'
];

/**
 * Extracts LayoutBaseProps' CSS-driven keys from a larger props object, returning
 * [baseProps, rest]. Called first, before pickSpacingProps, in each primitive.
 */
export function pickBaseProps<T extends LayoutBaseProps>(props: T) {
    return pickLayoutProps(props, LAYOUT_BASE_KEYS);
}

/**
 * Builds the responsive CSS array for base props (position, inset, top/right/bottom/left,
 * z-index, cursor, pointer-events, user-select, list-style). Shared by HBBox, HBFlex, HBGrid,
 * and HBSurface. `inset` and `top`/`right`/`bottom`/`left` have no token scale — same
 * raw-CSS-value treatment as `width`/`opacity`.
 *
 * `inset` (shorthand) is emitted before the four per-side longhands so they override it
 * via CSS cascade — mirrors the `borderWidth`/per-side and `overflow`/`overflowX` pattern.
 */
export function buildBaseCss(props: LayoutBaseProps) {
    return [
        responsiveValueCss(props.position, ['position']),
        responsiveValueCss(props.inset, ['inset']),
        responsiveValueCss(props.top, ['top']),
        responsiveValueCss(props.right, ['right']),
        responsiveValueCss(props.bottom, ['bottom']),
        responsiveValueCss(props.left, ['left']),
        responsiveValueCss(props.zIndex, ['z-index']),
        responsiveValueCss(props.cursor, ['cursor']),
        responsiveValueCss(props.pointerEvents, ['pointer-events']),
        responsiveValueCss(props.userSelect, ['user-select']),
        responsiveValueCss(props.listStyle, ['list-style'])
    ];
}

/** All LayoutSpacingProps keys. Used by pickSpacingProps to extract them from a larger object. */
const LAYOUT_SPACING_KEYS: Array<keyof LayoutSpacingProps> = [
    'padding',
    'paddingX',
    'paddingY',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'margin',
    'marginX',
    'marginY',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'width',
    'height'
];

/**
 * Extracts LayoutSpacingProps from a larger props object, returning
 * [spacingProps, rest]. Reduces destructuring boilerplate in components.
 */
export function pickSpacingProps<T extends LayoutSpacingProps>(props: T) {
    return pickLayoutProps(props, LAYOUT_SPACING_KEYS);
}

const LAYOUT_SIZING_KEYS: Array<keyof LayoutSizingProps> = [
    'maxWidth',
    'minWidth',
    'maxHeight',
    'minHeight',
    'overflow',
    'overflowX',
    'overflowY',
    'hideScrollbar',
    'objectFit'
];

/**
 * Extracts LayoutSizingProps from a larger props object, returning
 * [sizingProps, rest]. Called after pickSpacingProps.
 */
export function pickSizingProps<T extends LayoutSizingProps>(props: T) {
    return pickLayoutProps(props, LAYOUT_SIZING_KEYS);
}

const LAYOUT_CHILD_KEYS: Array<keyof LayoutChildProps> = [
    'flex',
    'flexGrow',
    'flexShrink',
    'flexBasis',
    'alignSelf',
    'order',
    'gridColumn',
    'gridRow',
    'justifySelf'
];

/**
 * Extracts LayoutChildProps from a larger props object, returning
 * [childProps, rest]. Used after pickSpacingProps to isolate self-placement props.
 */
export function pickChildProps<T extends LayoutChildProps>(props: T) {
    return pickLayoutProps(props, LAYOUT_CHILD_KEYS);
}

/**
 * Builds the responsive CSS array for self-placement props
 * (flex shorthand + longhands, alignSelf, order, grid placement).
 *
 * Flex shorthand emitted first; longhands follow and override via CSS cascade —
 * matching native CSS semantics. Flex and grid props coexist safely: the browser
 * ignores whichever set does not apply based on the parent container type.
 */
export function buildChildCss(props: LayoutChildProps) {
    const {
        flex,
        flexGrow,
        flexShrink,
        flexBasis,
        alignSelf,
        order,
        gridColumn,
        gridRow,
        justifySelf
    } = props;
    return [
        responsiveValueCss(flex, ['flex']),
        responsiveValueCss(flexGrow, ['flex-grow']),
        responsiveValueCss(flexShrink, ['flex-shrink']),
        responsiveValueCss(flexBasis, ['flex-basis']),
        responsiveValueCss(alignSelf, ['align-self']),
        responsiveValueCss(order, ['order']),
        responsiveValueCss(gridColumn, ['grid-column']),
        responsiveValueCss(gridRow, ['grid-row']),
        responsiveValueCss(justifySelf, ['justify-self'])
    ];
}

const LAYOUT_DECORATIVE_KEYS: Array<keyof LayoutDecorativeProps> = [
    'backgroundColor',
    'borderRadius',
    'boxShadow',
    'borderColor',
    'borderTopColor',
    'borderRightColor',
    'borderBottomColor',
    'borderLeftColor',
    'borderStyle',
    'borderTopStyle',
    'borderRightStyle',
    'borderBottomStyle',
    'borderLeftStyle',
    'borderWidth',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'opacity',
    'transform',
    'backgroundImage',
    'backgroundSize',
    'backgroundRepeat',
    'backgroundPosition'
];

/**
 * Extracts LayoutDecorativeProps from a larger props object, returning
 * [decorativeProps, rest]. Called after pickChildProps in HBBox.
 */
export function pickDecorativeProps<T extends LayoutDecorativeProps>(props: T) {
    return pickLayoutProps(props, LAYOUT_DECORATIVE_KEYS);
}

function resolveRadius(key: RadiusKey | undefined): string | undefined {
    if (key === undefined) {
        return undefined;
    }
    return vars.radius[key];
}

/**
 * Resolves a namespaced `"{category}.{key}"` color token to its CSS variable.
 * `'transparent'` is passed through untouched — it is a CSS-wide keyword rather than a
 * semantic color, so it has no entry in `semanticColors`. Reachable from
 * `backgroundColor` and from every border color prop — all of them accept it.
 */
function resolveColorToken(
    token: BackgroundColorToken | undefined
): string | undefined {
    if (token === undefined) {
        return undefined;
    }
    if (token === 'transparent') {
        return 'transparent';
    }
    const [category, key] = token.split('.') as [
        keyof typeof vars.colors,
        string
    ];
    return (vars.colors[category] as Record<string, string>)[key];
}

function resolveShadow(key: ShadowKey | undefined): string | undefined {
    if (key === undefined) {
        return undefined;
    }
    return vars.shadow[key];
}

function resolveBorderWidth(
    key: BorderWidthKey | undefined
): string | undefined {
    if (key === undefined) {
        return undefined;
    }
    return borderWidth[key];
}

/** Per-side border prop names paired with the CSS longhands they emit. */
const BORDER_SIDES = [
    {
        color: 'borderTopColor',
        style: 'borderTopStyle',
        width: 'borderTopWidth',
        colorCss: 'border-top-color',
        styleCss: 'border-top-style',
        widthCss: 'border-top-width'
    },
    {
        color: 'borderRightColor',
        style: 'borderRightStyle',
        width: 'borderRightWidth',
        colorCss: 'border-right-color',
        styleCss: 'border-right-style',
        widthCss: 'border-right-width'
    },
    {
        color: 'borderBottomColor',
        style: 'borderBottomStyle',
        width: 'borderBottomWidth',
        colorCss: 'border-bottom-color',
        styleCss: 'border-bottom-style',
        widthCss: 'border-bottom-width'
    },
    {
        color: 'borderLeftColor',
        style: 'borderLeftStyle',
        width: 'borderLeftWidth',
        colorCss: 'border-left-color',
        styleCss: 'border-left-style',
        widthCss: 'border-left-width'
    }
] as const;

type BorderStyleMap = Partial<Record<Breakpoint, BorderStyleKey>>;

function toBreakpointMap<T extends string>(
    value: Responsive<T> | undefined
): Partial<Record<Breakpoint, T>> {
    if (value === undefined) {
        return {};
    }
    return isResponsive(value) ? value : { initial: value };
}

/**
 * Resolves the `border-style` one side should carry: the style declared for it (per-side
 * prop, else the `borderStyle` shorthand, else `'solid'`) at every breakpoint where that
 * side has a declared width, and nothing at all when it has no width anywhere.
 *
 * Width is the switch that turns a border on. Resolving style per side rather than
 * globally is what makes a lone per-side width prop work: `border-style`'s CSS initial
 * value is `none`, and a `none` side draws nothing whatever its width, so the three
 * untouched sides stay invisible without a `border-width: 0` reset.
 *
 * Breakpoints are mobile-first, so a declaration holds for every wider breakpoint until
 * something overrides it — only the breakpoints where the width or the style actually
 * changes need a rule of their own.
 */
function sideBorderStyle(
    widthShorthand: Responsive<BorderWidthKey> | undefined,
    sideWidth: Responsive<BorderWidthKey> | undefined,
    styleShorthand: Responsive<BorderStyleKey> | undefined,
    sideStyle: Responsive<BorderStyleKey> | undefined
): BorderStyleMap | undefined {
    const widths = {
        ...toBreakpointMap(widthShorthand),
        ...toBreakpointMap(sideWidth)
    };
    const firstWidth = BREAKPOINT_ORDER.findIndex(
        breakpoint => widths[breakpoint] !== undefined
    );
    if (firstWidth === -1) {
        return undefined;
    }

    const styles = {
        ...toBreakpointMap(styleShorthand),
        ...toBreakpointMap(sideStyle)
    };
    const resolved: BorderStyleMap = {};
    let inherited: BorderStyleKey = 'solid';

    BREAKPOINT_ORDER.forEach((breakpoint, index) => {
        inherited = styles[breakpoint] ?? inherited;
        const changesHere =
            widths[breakpoint] !== undefined ||
            styles[breakpoint] !== undefined;
        if (index >= firstWidth && changesHere) {
            resolved[breakpoint] = inherited;
        }
    });

    return resolved;
}

function sameBorderStyle(
    a: BorderStyleMap | undefined,
    b: BorderStyleMap | undefined
): boolean {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    return BREAKPOINT_ORDER.every(
        breakpoint => a[breakpoint] === b[breakpoint]
    );
}

/**
 * Builds the responsive CSS array for decorative props (backgroundColor, borderRadius,
 * boxShadow, borderColor, borderStyle, borderWidth + their per-side overrides, opacity).
 * All values except `borderStyle` and `opacity` are resolved from design token keys to
 * CSS strings; `borderStyle` and `opacity` pass through raw. `borderColor` and its four
 * per-side props share the same `resolveColorToken` resolver as `backgroundColor`, over
 * the namespaced `"{category}.{key}"` token across `surface`/`base`/`text`/`stroke` plus
 * the bare keyword `'transparent'`.
 *
 * Each border axis emits its shorthand before its four longhands, so the per-side props
 * override it via CSS cascade — mirroring the `flex`/`flexGrow` and `overflow`/`overflowX`
 * patterns.
 *
 * `border-style` is the exception: it is resolved per side by `sideBorderStyle` and only
 * emitted for sides that have a declared width, which is what keeps `borderBottomWidth`
 * alone from drawing three unwanted `medium`-width edges. The four longhands collapse back
 * to a single `border-style` shorthand whenever all sides agree, which is every case that
 * does not use a per-side width or style prop.
 *
 * `transform` has no token scale either and passes through raw, same as `opacity`.
 *
 * The four `backgroundImage`/`backgroundSize`/`backgroundRepeat`/`backgroundPosition`
 * props also pass through raw. They are emitted after `backgroundColor` but never collide
 * with it: no `background` shorthand is exposed, precisely because the shorthand resets
 * `background-color` and would let one prop silently clobber the other. `backgroundSize`,
 * `backgroundRepeat`, and `backgroundPosition` are inert without `backgroundImage`.
 *
 * Used only by HBBox — HBFlex and HBGrid are scoped layout components without decorative props.
 */
export function buildDecorativeCss(props: LayoutDecorativeProps) {
    const sideStyles = BORDER_SIDES.map(side =>
        sideBorderStyle(
            props.borderWidth,
            props[side.width],
            props.borderStyle,
            props[side.style]
        )
    );
    const borderStyleCss = sideStyles.every(sideStyle =>
        sameBorderStyle(sideStyle, sideStyles[0])
    )
        ? [responsiveValueCss(sideStyles[0], ['border-style'])]
        : BORDER_SIDES.map((side, index) =>
              responsiveValueCss(sideStyles[index], [side.styleCss])
          );

    return [
        responsiveCss(
            props.backgroundColor,
            ['background-color'],
            resolveColorToken
        ),
        responsiveCss(props.borderRadius, ['border-radius'], resolveRadius),
        responsiveCss(props.boxShadow, ['box-shadow'], resolveShadow),
        responsiveCss(props.borderColor, ['border-color'], resolveColorToken),
        ...BORDER_SIDES.map(side =>
            responsiveCss(props[side.color], [side.colorCss], resolveColorToken)
        ),
        ...borderStyleCss,
        responsiveCss(props.borderWidth, ['border-width'], resolveBorderWidth),
        ...BORDER_SIDES.map(side =>
            responsiveCss(
                props[side.width],
                [side.widthCss],
                resolveBorderWidth
            )
        ),
        responsiveValueCss(props.opacity, ['opacity']),
        responsiveValueCss(props.transform, ['transform']),
        responsiveValueCss(props.backgroundImage, ['background-image']),
        responsiveValueCss(props.backgroundSize, ['background-size']),
        responsiveValueCss(props.backgroundRepeat, ['background-repeat']),
        responsiveValueCss(props.backgroundPosition, ['background-position'])
    ];
}

/**
 * Builds the responsive CSS array for spacing props (padding, margin, width, height).
 * Shared by HBBox, HBFlex, and HBGrid.
 */
export function buildSpacingCss(props: LayoutSpacingProps) {
    return [
        responsiveSpacingCss(props.padding, ['padding']),
        responsiveSpacingCss(props.paddingX, ['padding-left', 'padding-right']),
        responsiveSpacingCss(props.paddingY, ['padding-top', 'padding-bottom']),
        responsiveSpacingCss(props.paddingTop, ['padding-top']),
        responsiveSpacingCss(props.paddingRight, ['padding-right']),
        responsiveSpacingCss(props.paddingBottom, ['padding-bottom']),
        responsiveSpacingCss(props.paddingLeft, ['padding-left']),
        responsiveSpacingCss(props.margin, ['margin']),
        responsiveSpacingCss(props.marginX, ['margin-left', 'margin-right']),
        responsiveSpacingCss(props.marginY, ['margin-top', 'margin-bottom']),
        responsiveSpacingCss(props.marginTop, ['margin-top']),
        responsiveSpacingCss(props.marginRight, ['margin-right']),
        responsiveSpacingCss(props.marginBottom, ['margin-bottom']),
        responsiveSpacingCss(props.marginLeft, ['margin-left']),
        responsiveValueCss(props.width, ['width']),
        responsiveValueCss(props.height, ['height'])
    ];
}

/**
 * Builds the responsive CSS array for sizing-constraint props
 * (maxWidth, minWidth, maxHeight, minHeight, overflow, overflowX, overflowY).
 * Shared by HBBox, HBFlex, and HBGrid.
 * No token resolution — same raw-CSS-value treatment as width/height in buildSpacingCss.
 *
 * `overflow` shorthand is emitted first; `overflowX`/`overflowY` longhands follow
 * and override the shorthand's corresponding axis via CSS cascade when both are set —
 * mirrors the flex shorthand/longhand pattern in buildChildCss.
 *
 * `objectFit` only affects replaced content (`as="img"`/`as="video"`); it is inert on a
 * plain `div`, same as the flex-child props are inert outside a flex container.
 *
 * `hideScrollbar` is a plain boolean (no responsive/token treatment) that returns the shared
 * `hideScrollbar` mixin from `styles/scrollbar.ts` when true, and nothing when
 * false/undefined. It delegates rather than inlining the rules so the vendor-prefix list
 * stays in one place (HBTable's scroll container uses the same mixin).
 */
export function buildSizingCss(props: LayoutSizingProps) {
    return [
        responsiveValueCss(props.maxWidth, ['max-width']),
        responsiveValueCss(props.minWidth, ['min-width']),
        responsiveValueCss(props.maxHeight, ['max-height']),
        responsiveValueCss(props.minHeight, ['min-height']),
        responsiveValueCss(props.overflow, ['overflow']),
        responsiveValueCss(props.overflowX, ['overflow-x']),
        responsiveValueCss(props.overflowY, ['overflow-y']),
        buildHideScrollbarCss(props.hideScrollbar),
        responsiveValueCss(props.objectFit, ['object-fit'])
    ];
}

/**
 * Builds the CSS for `hideScrollbar` — hides the browser scrollbar while keeping the
 * element scrollable. No token/responsive treatment, matching its boolean-only prop shape.
 */
function buildHideScrollbarCss(hideScrollbar: boolean | undefined) {
    return hideScrollbar ? hideScrollbarMixin : '';
}

const namedWrapperCache = new Map<string, FC<{ children?: ReactNode }>>();

/**
 * Wraps `content` in a stable, name-keyed Fragment component whose `displayName` is set to
 * `name`, so it shows up as a named node in the React DevTools component tree instead of
 * the generic `HBBox`/`HBFlex`/`HBGrid` label. Returns `content` unchanged when `name` is
 * undefined. Shared by HBBox, HBFlex, HBGrid, and HBSurface for their `displayName` prop.
 *
 * The wrapper is cached per name — creating a new component type on every render would
 * force React to unmount/remount the wrapped subtree instead of just re-rendering it.
 */
export function withDisplayName(
    name: string | undefined,
    content: ReactElement
): ReactElement {
    if (!name) {
        return content;
    }
    let Wrapper = namedWrapperCache.get(name);
    if (!Wrapper) {
        Wrapper = ({ children }) => createElement(Fragment, null, children);
        Wrapper.displayName = name;
        namedWrapperCache.set(name, Wrapper);
    }
    return createElement(Wrapper, null, content);
}
